import { useCallback, useEffect, useMemo, useState } from "react";
import { useRouter } from "next/router";
import Slider from "react-slick";
import { FaQuoteRight, FaStar } from "react-icons/fa";
import useIsDesktop from "@/hooks/useIsDesktop";
import { fetchReviewsList } from "@/services/api/common";
import WriteReviewModal from "@/Component/Common/CustomerReviews/WriteReviewModal";
import ReviewAvatar from "@/Component/Common/CustomerReviews/ReviewAvatar";
import {
  collectTravelerGalleryImages,
  formatCompactCount,
  formatReviewDateFull,
  getRatingBreakdownRows,
} from "@/Component/Common/CustomerReviews/reviewUtils";
import styles from "./CustomerReviewsFull.module.css";
import Image from "next/image";
import { BiSolidEditAlt } from "react-icons/bi";
import GalleryModal from "@/Component/Module/HolidayDetail/GalleryModal";
import appendLoopingSlickDots from "@/Common/LoopingSlickDots";

const DESKTOP_PREVIEW_COUNT = 3;
const MOBILE_CAROUSEL_LIMIT = 10;

const mobileSliderSettings = {
  dots: true,
  infinite: true,
  speed: 400,
  slidesToShow: 1,
  slidesToScroll: 1,
  arrows: false,
  swipe: true,
  draggable: true,
  touchMove: true,
  touchThreshold: 8,
  adaptiveHeight: true,
};

function Stars({ count, className = "" }) {
  const safeCount = Math.max(0, Math.min(5, Math.round(Number(count) || 0)));

  return (
    <div className={`${styles.stars} ${className}`.trim()} aria-label={`${safeCount} out of 5 stars`}>
      {Array.from({ length: safeCount }).map((_, index) => (
        <FaStar key={index} color="#FCAB45" size={14} />
      ))}
    </div>
  );
}

function RatingSummary({ summary }) {
  const averageRating = Number(summary?.averageRating || 0).toFixed(1);
  const ratingLabel = summary?.ratingLabel || "Excellent";
  const totalReviews = summary?.totalReviews || 0;
  const totalRatings = summary?.totalRatings || totalReviews;


  return (
    <div className={styles.summaryLeft}>
      <div className={styles.summaryTop}>
        <div className={styles.scoreBadge}>
          <span className={styles.scoreValue}>{averageRating}</span>
          <FaStar color="#FCAB45" size={20} aria-hidden="true" />
        </div>
        <div className={styles.summaryCopy}>
          <p className={styles.summaryLabel}>{ratingLabel}</p>
          <p className={styles.summaryMeta}>
            {totalReviews} User Reviews and {totalRatings} Ratings
          </p>
        </div>
      </div>


    </div>
  );
}

function TravelerGallery({ images = [], onOpenGallery }) {
  if (!images.length) return null;

  const mainImage = images[0];
  const gridImages = images.slice(1, 5);
  const hasMore = images.length > 5;

  const handleSeeAll = () => {
    onOpenGallery?.(images[Math.min(4, images.length - 1)] || mainImage);
  };

  return (
    <div className={styles.galleryBlock}>
      <h3 className={styles.galleryTitle}>Traveler Image Gallery</h3>
      <div className={styles.galleryGrid}>
        <button
          type="button"
          className={styles.galleryMain}
          onClick={() => onOpenGallery?.(mainImage)}
          aria-label="Open traveler photo gallery"
        >
          <img src={mainImage} alt="" loading="lazy" />
        </button>
        <div className={styles.galleryThumbs}>
          {gridImages.map((image, index) => {
            const isLast = index === gridImages.length - 1;
            const showOverlay = isLast && hasMore;

            return (
              <div key={image} className={styles.galleryThumb}>
                {showOverlay ? (
                  <button
                    type="button"
                    className={styles.galleryThumbButton}
                    onClick={handleSeeAll}
                    aria-label="See all traveler photos"
                  >
                    <img src={image} alt="" loading="lazy" />
                    <span className={styles.galleryOverlay}>See All Photos</span>
                  </button>
                ) : (
                  <button
                    type="button"
                    className={styles.galleryThumbButton}
                    onClick={() => onOpenGallery?.(image)}
                    aria-label="Open traveler photo gallery"
                  >
                    <img src={image} alt="" loading="lazy" />
                  </button>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

function FullReviewCard({ review }) {
  const images = review.reviewImages || [];

  return (
    <article className={styles.reviewCard}>
      <Image src={"/assets/icons/Quotes.svg"} alt="" height={50} width={50} className={styles.quoteIcon} quality={70} loading="lazy" />

      <div className={styles.reviewHeader}>
        <ReviewAvatar
          imageUrl={review.userImageUrl}
          name={review.userName}
          size={44}
          className={styles.reviewAvatar}
        />
        <div className={styles.reviewUser}>
          <p className={styles.reviewName}>{review.userName}</p>
          <p className={styles.reviewDate}>
            Reviewed on: {formatReviewDateFull(review.reviewedOn)}
          </p>
        </div>
      </div>

      <Stars count={review.rating} className={styles.reviewStars} />
      <p className={styles.reviewText}>{review.reviewText}</p>

      {images.length ? (
        <div className={styles.reviewImages}>
          {images.slice(0, 5).map((image) => (
            <img key={image} src={image} alt="" loading="lazy" />
          ))}
        </div>
      ) : null}
    </article>
  );
}

function MobileReviewCarousel({ reviews = [] }) {
  const slides = reviews.slice(0, MOBILE_CAROUSEL_LIMIT);

  if (!slides.length) return null;

  if (slides.length === 1) {
    return (
      <div className={styles.mobileCarousel}>
        <FullReviewCard review={slides[0]} />
      </div>
    );
  }

  return (
    <div className={styles.mobileCarousel}>
      <Slider
        {...mobileSliderSettings}
        infinite={slides.length > 1}
        dotsClass={`slick-dots ${styles.carouselDots}`}
        appendDots={appendLoopingSlickDots(`slick-dots ${styles.carouselDots}`)}
      >
        {slides.map((review) => (
          <div key={review.reviewId} className={styles.mobileSlide}>
            <FullReviewCard review={review} />
          </div>
        ))}
      </Slider>
    </div>
  );
}

export default function CustomerReviewsFull({
  moduleType,
  slug: slugProp = "",
  initialReviewsList = null,
  skipAutoFetch = false,
  onReviewsReload,
  className = "",
}) {
  const router = useRouter();
  const { isDesktop, mounted } = useIsDesktop(900);
  const routerSlug = router.query.slug;
  const slug =
    slugProp ||
    (Array.isArray(routerSlug) ? routerSlug[0] : routerSlug) ||
    "";

  const [reviewsList, setReviewsList] = useState(initialReviewsList);
  const [isLoaded, setIsLoaded] = useState(initialReviewsList != null);
  const [showWriteModal, setShowWriteModal] = useState(false);
  const [showAllReviews, setShowAllReviews] = useState(false);
  const [travelerGalleryOpen, setTravelerGalleryOpen] = useState(false);
  const [travelerGalleryInitialSrc, setTravelerGalleryInitialSrc] = useState("");

  const loadReviews = useCallback(() => {
    return fetchReviewsList({ moduleType, slug })
      .then((response) => {
        setReviewsList(response?.data || null);
      })
      .catch((error) => {
        console.error("Failed to load reviews list:", error);
        setReviewsList(null);
      })
      .finally(() => {
        setIsLoaded(true);
      });
  }, [moduleType, slug]);

  useEffect(() => {
    if (initialReviewsList != null) {
      setReviewsList(initialReviewsList);
      setIsLoaded(true);
      return;
    }
    if (skipAutoFetch) return;
    loadReviews();
  }, [initialReviewsList, skipAutoFetch, loadReviews]);

  const summary = reviewsList?.summary || {};
  const reviews = reviewsList?.reviews || [];
  const galleryImages = useMemo(
    () => collectTravelerGalleryImages(reviews),
    [reviews]
  );
  const travelerGalleryCategories = useMemo(
    () => ({
      all: galleryImages.map((src, index) => ({
        id: `traveler-${index}-${src}`,
        src,
        alt: "Traveler photo",
        category: "all",
      })),
    }),
    [galleryImages]
  );

  const openTravelerGallery = (initialSrc = galleryImages[0] || "") => {
    setTravelerGalleryInitialSrc(initialSrc);
    setTravelerGalleryOpen(true);
  };
  const visibleHighlights =
    moduleType === "Holiday"
      ? [
        "Vacations",
        "Travel Breaks",
        "Getaways",
        "Festive Season",
        "Seasonal Travel",
      ]
      : moduleType === "Activity"
        ? [
          "Experiences",
          "Leisure Options",
          "Things to do",
          "Local Attractions",
          "Entertainment Ideas",
        ]
        : moduleType === "Staycation"
          ? [
            "Local Getaway",
            "Home Vacation",
            "Relaxation At Home",
            "Domestic Retreat",
            "Local Tourism",
          ]
          : [
            "Comfort",
            "Luxurious breakfast",
            "Top-class facilities",
            "Friendly Staff",
            "Prime Locations",
          ];
  const desktopReviews = showAllReviews
    ? reviews
    : reviews.slice(0, DESKTOP_PREVIEW_COUNT);
  const hasItems = reviews.length > 0;
  const showEmpty = isLoaded && !hasItems;
  const breakdownRows = getRatingBreakdownRows(
    summary?.ratingsBreakdown,
    summary?.totalRatings || summary?.totalReviews
  );
  if (!moduleType) return null;

  return (
    <div className={`standard-padding ${className}`.trim()}>
      <section className={styles.section} aria-labelledby="customer-reviews-full-title">
        <div className={styles.header}>
          <h2 id="customer-reviews-full-title" className="standard-heading">
            {mounted && !isDesktop ? "Customer Review" : "Customer Reviews"}
          </h2>
          <>
            <button
              type="button"
              className={styles.writeBtn}
              onClick={() => setShowWriteModal(true)}
            >
              Write a Review
            </button>
            <button
              type="button"
              className={styles.writeBtnMobile}
              onClick={() => setShowWriteModal(true)}
            >
              <BiSolidEditAlt />
              Write a Review
            </button>
          </>
        </div>

        {showEmpty ? (
          <p className="nodata">No data found</p>
        ) : hasItems ? (
          <>
            {mounted && isDesktop ? (
              <>
                <div className={styles.summaryPanel}>
                  <div className={styles.summaryColumn}>
                    <RatingSummary summary={summary} />
                    <div className={styles.breakdownList}>
                      {breakdownRows.map(({ star, count, percent }) => (
                        <div key={star} className={styles.breakdownRow}>
                          <div className={styles.breakdownStar}>
                            <span className={styles.breakdownStarCount}>{star}</span>
                            <FaStar color="#424753" size={14} aria-hidden="true" />
                          </div>
                          <div className={styles.breakdownTrack}>
                            <span
                              className={styles.breakdownFill}
                              style={{ width: `${percent}%` }}
                            />
                          </div>
                          <span className={styles.breakdownCount}>{formatCompactCount(count)}</span>
                        </div>
                      ))}
                    </div>
                    {visibleHighlights.length ? (
                      <div className={styles.highlightsBlock}>
                        <h3 className={styles.highlightsTitle}>
                          What people love about this package?
                        </h3>
                        <div className={styles.highlightTags}>
                          {visibleHighlights.map((item) => (
                            <span key={item} className={styles.highlightTag}>
                              {item}
                            </span>
                          ))}
                        </div>
                      </div>
                    ) : null}
                  </div>
                  <TravelerGallery
                    images={galleryImages}
                    onOpenGallery={openTravelerGallery}
                  />
                </div>

                <div className={styles.reviewGrid}>
                  {desktopReviews.map((review) => (
                    <FullReviewCard key={review.reviewId} review={review} />
                  ))}
                </div>

                {reviews.length > DESKTOP_PREVIEW_COUNT ? (
                  <div className={styles.viewAllWrap}>
                    <button
                      type="button"
                      className={styles.viewAllBtn}
                      onClick={() => setShowAllReviews((prev) => !prev)}
                    >
                      {showAllReviews ? "Show less reviews" : "View all reviews"}
                    </button>
                  </div>
                ) : null}
              </>
            ) : (
              <>
                <div className={styles.mobileSummary}>
                  <div className={styles.scoreBadge}>
                    <span className={styles.scoreValue}>
                      {Number(summary.averageRating || 0).toFixed(1)}
                    </span>
                    <FaStar color="#FCAB45" size={18} aria-hidden="true" />
                  </div>
                  <div className={styles.summaryCopy}>
                    <p className={styles.summaryLabel}>
                      {summary.ratingLabel || "Excellent"}
                    </p>
                    <p className={styles.summaryMeta}>
                      {summary.totalReviews || 0} User Reviews and{" "}
                      {summary.totalRatings || summary.totalReviews || 0} Ratings
                    </p>
                  </div>
                </div>

                <MobileReviewCarousel reviews={reviews} />
              </>
            )}
          </>
        ) : null}

        <WriteReviewModal
          open={showWriteModal}
          onClose={() => setShowWriteModal(false)}
          moduleType={moduleType}
          slug={slug}
          onSuccess={onReviewsReload || loadReviews}
          allowModuleSelect
        />

        <GalleryModal
          open={travelerGalleryOpen}
          onClose={() => setTravelerGalleryOpen(false)}
          title="Traveler Image Gallery"
          categories={travelerGalleryCategories}
          initialCategory="all"
          initialImageSrc={travelerGalleryInitialSrc}
          hideCategoryTabs
        />
      </section>
    </div>
  );
}
